import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Heart, Share2, Truck, Shield, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { estimateArrivalWindow, formatArrivalWindow } from '../../lib/deliveryEta';
import { DEFAULT_DELIVERY_RADIUS_MILES, distanceMiles, formatMiles } from '../../lib/geo';
import { motion, AnimatePresence } from 'framer-motion';
import { useCartContext } from '../../hooks/useCart';
import { useFavoritesContext } from '../../hooks/useFavorites';
import { useProduct } from '../../hooks/useProducts';
import { useBoutique, boutiqueCoords } from '../../hooks/useBoutiques';
import { useShopperLocation } from '../../hooks/useShopperLocation';
import { useAuth } from '../../hooks/useAuth';
import { logAnalyticsEvent } from '../../services/analyticsLogger';
import { ShopperNavActions } from '../../components/shopper/ShopperNavActions';
import { BackButton } from '../../components/ui/BackButton';
import { SHOPPER_HOME } from '../../constants/routes';

export function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const cart = useCartContext();
  const favorites = useFavoritesContext();
  
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const { product, loading } = useProduct(productId || null);
  const isFavorited = product ? favorites.isFavorited(product.id) : false;

  useEffect(() => {
    if (product?.brandId && productId) {
      logAnalyticsEvent('product_view', product.brandId, { productId, shopperId: user?.uid });
    }
  }, [product?.brandId, productId, user?.uid]);

  const handleAddToBag = () => {
    if (!selectedSize || !product) return;

    const added = cart.addItem({
      productId: product.id,
      name: product.name,
      brand: product.brand,
      businessId: product.brandId,
      price: product.price,
      size: selectedSize,
      quantity: 1,
      imageUrl: product.images[0],
    });
    if (!added) return; // shopper kept their existing bag

    logAnalyticsEvent('add_to_bag', product.brandId, { productId: product.id });
    setShowConfetti(true);

    setTimeout(() => {
      setShowConfetti(false);
      navigate('/bag');
    }, 1500);
  };

  const handleShare = async () => {
    if (!product) return;
    const shareData = {
      title: `${product.name} — ${product.brand} on FASH`,
      text: `Check out ${product.name} from ${product.brand}`,
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
        logAnalyticsEvent('product_share', product.brandId, { productId: product.id });
      } catch {
        // Shopper dismissed the share sheet — not an error
      }
      return;
    }

    // Fallback: copy the link
    try {
      await navigator.clipboard.writeText(shareData.url);
      logAnalyticsEvent('product_share', product.brandId, { productId: product.id });
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Clipboard unavailable (insecure context) — nothing we can do
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-serif text-heading mb-4">Product not found</h2>
          <p className="text-body mb-6">The product you're looking for doesn't exist.</p>
          <Button onClick={() => navigate(SHOPPER_HOME)}>Return Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <BackButton fallback={product.brandId ? `/boutique/${product.brandId}` : SHOPPER_HOME} />
            <div className="flex items-center space-x-2">
              <button 
                onClick={() => product && favorites.toggleFavorite({
                  productId: product.id,
                  name: product.name,
                  brand: product.brand,
                  price: product.price,
                  imageUrl: product.images[0],
                })}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <Heart className={cn("h-6 w-6", isFavorited ? "fill-primary text-primary" : "text-heading")} />
              </button>
              <div className="relative">
                <button
                  onClick={handleShare}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Share product"
                >
                  <Share2 className="h-6 w-6 text-heading" />
                </button>
                {linkCopied && (
                  <span className="absolute top-full right-0 mt-1 px-2 py-1 bg-heading text-white text-xs rounded-lg whitespace-nowrap">
                    Link copied!
                  </span>
                )}
              </div>
              <ShopperNavActions className="p-2 hover:bg-gray-100 rounded-full transition-colors" iconClassName="h-6 w-6 text-heading" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto">
        <div className="lg:grid lg:grid-cols-2 lg:gap-12">
          {/* Image Gallery */}
          <div className="relative">
            {/* Main Image */}
            <div className="aspect-[3/4] overflow-hidden bg-gray-100">
              <AnimatePresence mode="wait">
                <motion.img
                  key={selectedImage}
                  src={product.images[selectedImage]}
                  alt={product.name}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full h-full object-cover"
                />
              </AnimatePresence>
            </div>
            
            {/* Thumbnail Navigation */}
            {product.images.length > 1 && (
              <div className="flex space-x-3 p-4 overflow-x-auto">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={cn(
                      "flex-shrink-0 w-20 h-24 rounded-lg overflow-hidden border-2 transition-colors",
                      selectedImage === idx ? "border-primary" : "border-transparent"
                    )}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="px-4 py-6 lg:py-12">
            <p className="text-sm font-medium text-primary uppercase tracking-wider mb-2">
              {product.brand}
            </p>
            <h1 className="text-3xl font-serif text-heading mb-4">
              {product.name}
            </h1>
            {product.compareAtPrice && product.compareAtPrice > product.price ? (
              <div className="mb-6 flex items-center gap-3">
                <p className="text-lg text-gray-400 line-through">
                  ${product.compareAtPrice.toFixed(2)}
                </p>
                <p className="text-2xl font-medium text-red-600">
                  ${product.price.toFixed(2)}
                </p>
                <span className="px-2 py-0.5 bg-red-100 text-red-700 text-xs font-bold rounded-full">
                  {Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)}% OFF
                </span>
              </div>
            ) : (
              <p className="text-2xl font-medium text-heading mb-6">
                ${product.price.toFixed(2)}
              </p>
            )}

            <p className="text-body leading-relaxed mb-8">
              {product.description}
            </p>

            {/* Size Selector */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium text-heading">Select Size</span>
                <button className="text-sm text-primary font-medium">Size Guide</button>
              </div>
              <div className="flex flex-wrap gap-3">
                {product.sizes.map((size) => (
                  <button
                    key={size.label}
                    onClick={() => size.available && setSelectedSize(size.label)}
                    disabled={!size.available}
                    className={cn(
                      "min-w-[3.5rem] h-14 px-4 rounded-full font-medium transition-all duration-200",
                      selectedSize === size.label
                        ? "bg-primary text-white ring-2 ring-primary ring-offset-2"
                        : size.available
                          ? "bg-gray-100 text-heading hover:bg-gray-200"
                          : "bg-gray-50 text-gray-300 cursor-not-allowed line-through"
                    )}
                  >
                    {size.label}
                  </button>
                ))}
              </div>
              {product.sizes.some(s => !s.available) && (
                <p className="text-sm text-body mt-3">
                  Some sizes are currently unavailable
                </p>
              )}
            </div>

            {/* Delivery Info */}
            <DeliveryEstimate brandId={product.brandId} brand={product.brand} />

            {/* Add to Bag Button */}
            <div className="sticky bottom-0 bg-white pt-4 pb-6 border-t border-gray-100 -mx-4 px-4 lg:relative lg:border-0 lg:mx-0 lg:px-0">
              <Button
                size="lg"
                fullWidth
                onClick={handleAddToBag}
                disabled={!selectedSize}
                className="relative overflow-hidden"
              >
                {showConfetti ? (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="flex items-center"
                  >
                    ✨ Added to Bag!
                  </motion.span>
                ) : (
                  <>
                    Add to Bag • ${product.price.toFixed(2)}
                    {product.compareAtPrice && product.compareAtPrice > product.price && (
                      <span className="ml-1.5 text-white/60 line-through text-sm">${product.compareAtPrice.toFixed(2)}</span>
                    )}
                  </>
                )}
              </Button>
              
              {!selectedSize && (
                <p className="text-center text-sm text-body mt-2">
                  Please select a size
                </p>
              )}
            </div>

            {/* Trust Badges */}
            <div className="flex items-center justify-center space-x-6 pt-6 text-sm text-body">
              <div className="flex items-center space-x-2">
                <Shield className="h-4 w-4" />
                <span>Secure Payment</span>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

/**
 * Arrival estimate shown before the shopper commits to anything.
 *
 * There's no dropoff address at this point — the shopper may not even be
 * signed in — so an Uber quote isn't possible (and one per product view would
 * be wasteful anyway). This approximates from the boutique's own prep time and
 * how far away the shopper is, and says so. Checkout replaces it with the real
 * courier quote.
 */
function DeliveryEstimate({ brandId, brand }: { brandId: string; brand: string }) {
  const { boutique, loading: boutiqueLoading } = useBoutique(brandId || null);
  const { location, loading: locationLoading, label } = useShopperLocation();

  const loading = boutiqueLoading || locationLoading;

  const { title, detail, unavailable } = useMemo(() => {
    if (loading || !boutique) {
      return { title: 'Same-Day Delivery', detail: 'Checking arrival times…', unavailable: false };
    }

    if (boutique.closedNow) {
      return {
        title: 'Same-Day Delivery',
        detail: `${brand} is closed right now — order for their next opening`,
        unavailable: false,
      };
    }

    const store = boutiqueCoords(boutique);
    const shopper =
      typeof location?.lat === 'number' && typeof location?.lng === 'number'
        ? { lat: location.lat, lng: location.lng }
        : null;

    if (!store || !shopper) {
      return {
        title: 'Same-Day Delivery',
        detail: 'Set your location to see an arrival estimate',
        unavailable: false,
      };
    }

    const miles = distanceMiles(shopper.lat, shopper.lng, store.lat, store.lng);
    const radius = boutique.deliveryRadius || DEFAULT_DELIVERY_RADIUS_MILES;
    if (miles > radius) {
      return {
        title: 'Outside delivery range',
        detail: `${brand} doesn't deliver to ${label} yet — it's ${formatMiles(miles)} away`,
        unavailable: true,
      };
    }

    const window = formatArrivalWindow(
      estimateArrivalWindow({ prepMinutes: boutique.prepTimeEstimate, miles })
    );

    if (!window) {
      return { title: 'Same-Day Delivery', detail: `Delivered to ${label}`, unavailable: false };
    }

    return {
      title: `Arrives ${window}`,
      detail: `Same-day delivery to ${label} · exact time confirmed at checkout`,
      unavailable: false,
    };
  }, [loading, boutique, location?.lat, location?.lng, label, brand]);

  return (
    <div className={cn('rounded-2xl p-4 mb-8', unavailable ? 'bg-gray-100' : 'bg-purple-50')}>
      <div className="flex items-start space-x-3">
        <div
          className={cn(
            'p-2 bg-white rounded-lg',
            unavailable ? 'text-gray-400' : 'text-primary'
          )}
        >
          <Truck className="h-5 w-5" />
        </div>
        <div>
          <p className="font-medium text-heading">{title}</p>
          <p className="text-sm text-body">{detail}</p>
        </div>
      </div>
    </div>
  );
}
